// Splits an Activity alert preview into plain text, @mentions and (for access requests) the
// bold requested role. The "@" inside an email ("sam@acme.com") is not a mention: a mention's
// "@" never follows a letter, digit or address character. Unicode classes keep "@Iñigo" whole.
export interface PreviewPart {
  text: string;
  mention: boolean;
  strong: boolean;
}

const MENTION_RE = /(?<![\p{L}\p{N}\p{M}._%+-])@[\p{L}\p{N}\p{M}._-]+/gu;
const ROLE_RE = /(\brequested\s+)(.+?)(\s+access\s+to\b)/i;

// `names`: the roster's display names, so an agent's bare "@Maya Rivera" highlights the whole name
// (the longest roster name that starts at the "@" and ends on a word boundary), not just "@Maya".
export function previewParts(
  text: string,
  emphasizeRole = false,
  names: readonly string[] = [],
): PreviewPart[] {
  const parts: PreviewPart[] = [];
  const multi = names
    .map((n) => n.trim().toLowerCase())
    .filter((n) => n.includes(" "))
    .sort((a, b) => b.length - a.length);
  const lower = text.toLowerCase();
  let lastIndex = 0;
  for (const match of text.matchAll(MENTION_RE)) {
    const index = match.index ?? 0;
    if (index < lastIndex) continue; // inside a multi-word mention already taken
    if (index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, index), mention: false, strong: false });
    }
    const full = multi.find(
      (n) =>
        lower.startsWith(n, index + 1) &&
        !/[\p{L}\p{N}\p{M}_]/u.test(text.charAt(index + 1 + n.length)),
    );
    const length = full ? full.length + 1 : match[0].length;
    parts.push({ text: text.slice(index, index + length), mention: true, strong: false });
    lastIndex = index + length;
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), mention: false, strong: false });
  }
  if (!emphasizeRole) return parts;
  // Access-request previews ("… requested <role> access to <machine>"): bold the role.
  const out: PreviewPart[] = [];
  for (const part of parts) {
    const m = part.mention ? null : ROLE_RE.exec(part.text);
    if (!m) {
      out.push(part);
      continue;
    }
    const roleStart = m.index + m[1].length;
    const role = m[2];
    const before = part.text.slice(0, roleStart);
    const after = part.text.slice(roleStart + role.length);
    if (before) out.push({ text: before, mention: false, strong: false });
    out.push({ text: role, mention: false, strong: true });
    if (after) out.push({ text: after, mention: false, strong: false });
  }
  return out;
}

// The line of a message that names the reader, so their mention alert previews THEIR line, not
// another person's. undefined: no line names them (the message's start is the preview).
// TWIN: mentionLinePreview in packages/server/src/server/cyborg/message-push-preview.ts.
export function mentionLine(
  text: string,
  reader: { name?: string | null; email?: string | null } | null | undefined,
): string | undefined {
  const needles = [reader?.name, reader?.email?.split("@")[0]]
    .map((n) => n?.trim().toLowerCase())
    .filter((n): n is string => !!n)
    .map((n) => `@${n}`);
  const plain = text.replace(/@\[([^\]]+)\]/g, "@$1");
  return plain.split("\n").find((l) => needles.some((n) => namesReader(l.toLowerCase(), n)));
}

// "@bob" names the reader only as a whole name: never inside "@bobby".
function namesReader(line: string, needle: string): boolean {
  for (let at = line.indexOf(needle); at >= 0; at = line.indexOf(needle, at + 1)) {
    if (!/[\p{L}\p{N}_]/u.test(line.charAt(at + needle.length))) return true;
  }
  return false;
}
